import React, { useContext, useEffect, useState } from 'react';
import { RootStore, RootStoreContext } from '../../stores/rootStore';
import { observer } from 'mobx-react-lite';
import AuthenticatedPage from '../auth/authenticatedPage';
import InvoiceModal from '../invoice/invoice-modal';
import { saveVisit } from '../../api/visitTrackerService';
import { cancelSubscriptionPost, restoreSubscriptionPost, updatePaymentMethodPost } from '../../api/orderService';
import { SubscriptionDtoV3 } from '../../models/dtoTypes';

const fmtDate = (s: string | undefined | null) => {
    if (!s) return s;
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Subscriptions = observer(() => {
    const rootStore: RootStore = useContext(RootStoreContext);
    const subscriptionsStore = rootStore.subscriptionsStore;
    const { isLoggedIn } = rootStore.userStore;
    const { subscriptionsV3, loading } = subscriptionsStore;
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState<number | null>(null);
    const [manageSub, setManageSub] = useState<SubscriptionDtoV3 | null>(null);
    const [updatingCard, setUpdatingCard] = useState(false);

    //Stripe-hosted card-update page (billing portal, focused flow); the URL is one-time and
    //short-lived, so it is fetched on click and navigated to immediately
    const handleUpdateCard = async (subscriptionId: number) => {
        setUpdatingCard(true);
        try {
            const url = await updatePaymentMethodPost(subscriptionId);
            if (url) { window.location.href = url; return; }
            window.alert('Payment details cannot be updated right now. Please try again later.');
        } catch (e) {
            console.error('Failed to open the card-update page', e);
            window.alert('Payment details cannot be updated right now. Please try again later.');
        }
        setUpdatingCard(false);
    };

    //earliest order date = when the subscription started
    const subStart = (s: SubscriptionDtoV3): string | undefined => {
        const times = (s.orders || [])
            .map(o => o.orderDate)
            .filter((d): d is string => !!d)
            .map(d => new Date(d).getTime())
            .filter(t => !isNaN(t));
        return times.length ? new Date(Math.min(...times)).toISOString() : undefined;
    };

    //cancel the auto-renewing Stripe subscription (stays active until its end date, then won't renew)
    const handleCancel = async (subscriptionId: number) => {
        setCancelling(subscriptionId);
        try {
            await cancelSubscriptionPost(subscriptionId);
            await subscriptionsStore.fetchSubscriptionsV3();
            setManageSub(null);
            window.alert('Auto-renewal cancelled. Your subscription remains active until its end date.');
        } catch (e) {
            console.error('Failed to cancel subscription', e);
            window.alert('Sorry, we could not cancel the subscription. Please try again later.');
        } finally {
            setCancelling(null);
        }
    };

    //re-enable auto-renewal on a cancelled subscription
    const handleRestore = async (subscriptionId: number) => {
        setCancelling(subscriptionId);
        try {
            await restoreSubscriptionPost(subscriptionId);
            await subscriptionsStore.fetchSubscriptionsV3();
            setManageSub(null);
            window.alert('Auto-renewal restored. Your subscription will renew automatically.');
        } catch (e) {
            console.error('Failed to restore subscription', e);
            window.alert('Sorry, we could not restore auto-renewal. Please try again later.');
        } finally {
            setCancelling(null);
        }
    };

    const handleCopy = (code: string) => {
        navigator.clipboard?.writeText(code).catch(() => {});
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 1500);
    };

    useEffect(() => {
        document.title = 'Desktop Call Recorder — Subscriptions';
        saveVisit('subscriptions');
    }, []);

    useEffect(() => {
        if (!isLoggedIn) return;
        subscriptionsStore.fetchSubscriptionsV3();
    }, [isLoggedIn]);

    return (
        <AuthenticatedPage>
            <div className="page-wrap">
                <div className="page-head">
                    <div>
                        <h1>Your subscriptions</h1>
                        <p className="sub">Manage your Desktop Call Recorder Pro subscriptions, activation codes and invoices.</p>
                    </div>
                    <a href="/purchase" className="btn btn-primary">
                        <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="14" height="14"><path d="M12 5v14M5 12h14"/></svg>
                        New subscription
                    </a>
                </div>

                {loading ? (
                    <div className="page-center"><p>Getting your subscriptions…</p></div>
                ) : subscriptionsV3.length === 0 ? (
                    <div className="page-center">
                        <p>{subscriptionsStore.error ?? "You don't have any subscriptions yet."}</p>
                        <a href="/purchase" className="btn btn-primary">Get a subscription</a>
                    </div>
                ) : (
                    subscriptionsV3.map(subscription => {
                        const expired = subscription.isExpired || subscription.isBlocked;
                        //the renewal charge failed and Stripe is retrying: a payment problem, whether
                        //or not the licence has already lapsed (server-provided, never inferred here)
                        const paymentProblem = !subscription.isBlocked && subscription.billingStatus === 'past_due';
                        const bannerClass = expired ? 'danger' : subscription.renewalLink ? 'warn' : '';
                        const statusClass = subscription.isBlocked ? 'blocked' : paymentProblem ? 'past-due' : subscription.isExpired ? 'expired' : 'active';
                        const statusLabel = subscription.isBlocked ? 'Blocked' : paymentProblem ? 'Payment problem' : subscription.isExpired ? 'Expired' : 'Active';
                        //the manage strip (card update, cancel/restore) stays available while the
                        //Stripe subscription is live or past due - also after the licence lapsed
                        const showManage = !subscription.isBlocked && !!subscription.isRecurring
                            && (!expired || !!subscription.canUpdatePaymentMethod);

                        return (
                            <article key={subscription.id} className="sub-card">
                                {paymentProblem ? (
                                    <div className="sub-banner danger">
                                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                                        The renewal payment didn't go through. Pay the open invoice or update your card — Pro continues once the payment succeeds.
                                        {subscription.hostedInvoiceUrl && (
                                            <a href={subscription.hostedInvoiceUrl} className="sub-banner-cta">Pay now →</a>
                                        )}
                                        {subscription.canUpdatePaymentMethod && (
                                            <button type="button" className="sub-banner-cta btn-link" disabled={updatingCard}
                                                onClick={() => handleUpdateCard(subscription.id)}>
                                                {updatingCard ? 'Opening…' : 'Update payment details'}
                                            </button>
                                        )}
                                    </div>
                                ) : subscription.renewalLink && !(subscription.isRecurring && subscription.autoRenew !== false) && (
                                    <div className={`sub-banner ${bannerClass}`}>
                                        {expired ? (
                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                                        ) : (
                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                        )}
                                        {expired
                                            ? 'Your subscription has expired.'
                                            : 'Your subscription is about to expire. Renew before the expiry date.'}
                                        <a href={subscription.renewalLink} className="sub-banner-cta">Renew now →</a>
                                    </div>
                                )}

                                <div className="sub-head">
                                    <div>
                                        <div className="lbl">Product</div>
                                        <div className="product">Desktop Call Recorder {subscription.productName} Pro</div>
                                        <div className="product-sub">{subscription.billingPeriodMonths === 1 ? 'Monthly' : 'Annual'} subscription</div>
                                    </div>
                                    <div>
                                        <div className="lbl">Computers</div>
                                        <div className="val">{subscription.qnt}</div>
                                    </div>
                                    <div>
                                        <div className="lbl">{expired ? 'Expired' : 'Expires'}</div>
                                        <div className="val">{fmtDate(subscription.endDate)}</div>
                                    </div>
                                    <div>
                                        <div className="lbl">Status</div>
                                        <span className={`status-pill ${statusClass}`}>
                                            <span className="dot" />
                                            {statusLabel}
                                        </span>
                                    </div>
                                </div>

                                {showManage && (
                                    <div className="sub-manage">
                                        <span className={`renew-badge${subscription.autoRenew === false || paymentProblem ? ' off' : ''}`}>
                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                                            {paymentProblem ? 'Renewal payment failed' : subscription.autoRenew === false ? 'Auto-renew off' : 'Auto-renew on'}
                                        </span>
                                        <div className="sub-manage-actions">
                                            {subscription.autoRenew === false && (
                                                <button
                                                    type="button"
                                                    className="restore-btn"
                                                    disabled={cancelling === subscription.id}
                                                    onClick={() => { if (window.confirm('Restore auto-renewal? Your subscription will renew automatically again.')) handleRestore(subscription.id); }}
                                                >
                                                    {cancelling === subscription.id ? 'Restoring…' : 'Restore auto-renewal'}
                                                </button>
                                            )}
                                            <button type="button" className="manage-btn" onClick={() => setManageSub(subscription)}>
                                                Manage subscription
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {subscription.activationCodes.length > 0 && (
                                    <div className="activation">
                                        <div className="act-label">
                                            Activation {subscription.activationCodes.length === 1 ? 'code' : 'codes'}
                                        </div>
                                        <div className="code-list">
                                            {subscription.activationCodes.map((code, i) => (
                                                <div key={i} className="code-row">
                                                    <span className="code">{code.code}</span>
                                                    <button
                                                        type="button"
                                                        className={`copy-btn${copiedCode === code.code ? ' copied' : ''}`}
                                                        onClick={() => handleCopy(code.code)}
                                                    >
                                                        {copiedCode === code.code ? (
                                                            <>
                                                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20,6 9,17 4,12"/></svg>
                                                                Copied
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                                                Copy
                                                            </>
                                                        )}
                                                    </button>
                                                    {code.activations.length > 0
                                                        ? code.activations.map((act, j) => (
                                                            <span key={j} className="computer">Activated on <strong>{act.computerName}</strong></span>
                                                        ))
                                                        : <span className="computer unassigned">Not activated yet</span>
                                                    }
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {subscription.orders.length > 0 && (
                                    <>
                                        <div className="orders-head">Orders</div>
                                        <div className="orders">
                                            {subscription.orders.map(order => (
                                                <div key={order.id} className="order">
                                                    <div className="ord-id">
                                                        #{order.id}
                                                        <small>{fmtDate(order.orderDate)}</small>
                                                    </div>
                                                    <div>
                                                        <div className="lbl">Payment method</div>
                                                        <span className="v">{order.payment?.paymentType}</span>
                                                    </div>
                                                    <div>
                                                        <div className="lbl">Status</div>
                                                        <span className="status-tag">
                                                            <svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20,6 9,17 4,12"/></svg>
                                                            {order.payment?.paymentStatus}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="lbl">Date</div>
                                                        <span className="v">{fmtDate(order.payment?.dateTime)}</span>
                                                    </div>
                                                    <div>
                                                        <div className="lbl">Amount</div>
                                                        <span className="v" style={{fontWeight:700,color:'var(--text)'}}>{(order.payment?.currency?.toUpperCase() || 'GBP')} {order.payment?.amount}</span>
                                                    </div>
                                                    {order.payment?.invoiceUrl ? (
                                                        //Managed Payments: Stripe is the merchant of record and hosts the invoice
                                                        <a href={order.payment.invoiceUrl} target="_blank" rel="noopener noreferrer" className="inv-link">
                                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                            Invoice
                                                        </a>
                                                    ) : order.payment?.isManaged ? (
                                                        //Managed Payments but the hosted invoice link isn't ready yet - Stripe
                                                        //issues the invoice, so never fall back to the manual invoice form here
                                                        <span className="inv-link" style={{opacity:.5, cursor:'default'}} title="Stripe is preparing your invoice — it will appear here shortly.">
                                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                            Invoice pending
                                                        </span>
                                                    ) : (
                                                        <a href="" className="inv-link" onClick={e => {
                                                            e.preventDefault();
                                                            if (order.payment) subscriptionsStore.openInvoiceModal(order.payment);
                                                        }}>
                                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                            Invoice
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </article>
                        );
                    })
                )}

                {manageSub && (
                    <div className="manage-overlay" onClick={() => setManageSub(null)}>
                        <div className="manage-modal" onClick={e => e.stopPropagation()}>
                            <div className="manage-head">
                                <h3>Manage subscription</h3>
                                <button type="button" className="manage-close" onClick={() => setManageSub(null)} aria-label="Close">×</button>
                            </div>
                            <div className="manage-product">Desktop Call Recorder {manageSub.productName} Pro</div>
                            <div className="manage-rows">
                                <div><span className="lbl">Status</span><span className="val">{manageSub.billingStatus === 'past_due' ? 'Renewal payment failed' : manageSub.autoRenew === false ? 'Auto-renew off' : 'Auto-renewing'}</span></div>
                                <div><span className="lbl">Started</span><span className="val">{fmtDate(subStart(manageSub))}</span></div>
                                {manageSub.autoRenew === false ? (
                                    <div><span className="lbl">Active until</span><span className="val">{fmtDate(manageSub.endDate)}</span></div>
                                ) : (
                                    <>
                                        {/* Stripe's charge date is the renewal truth; the licence can be
                                            paid further ahead (credited time), shown separately */}
                                        <div><span className="lbl">Renews on</span><span className="val">{fmtDate(manageSub.nextChargeDate ?? manageSub.endDate)}</span></div>
                                        {manageSub.nextChargeDate && fmtDate(manageSub.nextChargeDate) !== fmtDate(manageSub.endDate) && (
                                            <div><span className="lbl">Paid until</span><span className="val">{fmtDate(manageSub.endDate)}</span></div>
                                        )}
                                    </>
                                )}
                                <div><span className="lbl">Computers</span><span className="val">{manageSub.qnt}</span></div>
                                <div>
                                    <span className="lbl">Payment method</span>
                                    <span className="val">
                                        <button type="button" className="btn-link" disabled={updatingCard}
                                            onClick={() => handleUpdateCard(manageSub.id)}>
                                            {updatingCard ? 'Opening…' : 'Update payment details'}
                                        </button>
                                    </span>
                                </div>
                            </div>
                            {manageSub.billingStatus === 'past_due' ? (
                                <>
                                    <p className="manage-note">
                                        The renewal payment didn't go through and Stripe is retrying. Pay the open invoice now,
                                        or update your payment details — future renewals charge the card on the subscription.
                                    </p>
                                    <div className="manage-actions">
                                        <button type="button" className="btn-ghost" onClick={() => setManageSub(null)}>Close</button>
                                        {manageSub.hostedInvoiceUrl && (
                                            <a href={manageSub.hostedInvoiceUrl} className="btn-primary">Pay now</a>
                                        )}
                                    </div>
                                </>
                            ) : manageSub.autoRenew === false ? (
                                <>
                                    <p className="manage-note">
                                        Auto-renewal is off. Your subscription stays active until {fmtDate(manageSub.endDate)}
                                        and then stops. You can turn renewal back on at any time before then.
                                    </p>
                                    <div className="manage-actions">
                                        <button type="button" className="btn-ghost" onClick={() => setManageSub(null)}>Close</button>
                                        <button
                                            type="button"
                                            className="btn-primary"
                                            disabled={cancelling === manageSub.id}
                                            onClick={() => handleRestore(manageSub.id)}
                                        >
                                            {cancelling === manageSub.id ? 'Restoring…' : 'Restore auto-renewal'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="manage-note">
                                        Cancelling stops the automatic {manageSub.billingPeriodMonths === 1 ? 'monthly' : 'yearly'} renewal. Your subscription stays active
                                        until {fmtDate(manageSub.endDate)}, then it won’t renew.
                                    </p>
                                    <div className="manage-actions">
                                        <button type="button" className="btn-ghost" onClick={() => setManageSub(null)}>Keep subscription</button>
                                        <button
                                            type="button"
                                            className="btn-danger"
                                            disabled={cancelling === manageSub.id}
                                            onClick={() => handleCancel(manageSub.id)}
                                        >
                                            {cancelling === manageSub.id ? 'Cancelling…' : 'Cancel auto-renewal'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                <InvoiceModal />
            </div>
        </AuthenticatedPage>
    );
});

export default Subscriptions;
